// Shared filter state for the Financial Report, persisted in the URL query string
// so that switching sub-tab preserves the current selection and the page is
// shareable / reloadable.
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { CurrencyMode } from '../CurrencySelector';
import type { PaymentMethodType } from '../PaymentMethodTypeFilter';

export type FinancialSubTab = 'overview' | 'deposits' | 'withdrawals' | 'mix' | 'ftd' | 'players';

export interface FinancialFilters {
  // Values
  subTab: FinancialSubTab;
  dateFrom: string;
  dateTo: string;
  siteIds: number[];
  currencyMode: CurrencyMode;
  pmType: PaymentMethodType | undefined;
  granularity: 'daily' | 'weekly' | 'monthly';
  dimensions: string[];
  playerSearch: string;
  // Setters (each updates the URL)
  setSubTab: (v: FinancialSubTab) => void;
  setDateFrom: (v: string) => void;
  setDateTo: (v: string) => void;
  setDateRange: (from: string, to: string) => void;
  setSiteIds: (v: number[]) => void;
  setCurrencyMode: (v: CurrencyMode) => void;
  setPmType: (v: PaymentMethodType | undefined) => void;
  setGranularity: (v: 'daily' | 'weekly' | 'monthly') => void;
  setDimensions: (v: string[]) => void;
  setPlayerSearch: (v: string) => void;
}

function defaultDateFrom(): string {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0];
}
function defaultDateTo(): string {
  return new Date().toISOString().split('T')[0];
}

export function useFinancialFilters(): FinancialFilters {
  const [params, setParams] = useSearchParams();

  // Build an updater that merges a partial patch into the current params.
  // We use the callback form of setParams to always read the latest URL state,
  // which avoids stale closures when multiple setters fire in the same tick.
  const patch = useCallback((updates: Record<string, string | null>) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [k, v] of Object.entries(updates)) {
        if (v === null || v === '') next.delete(k);
        else next.set(k, v);
      }
      return next;
    });
  }, [setParams]);

  return useMemo<FinancialFilters>(() => {
    const subTabRaw = params.get('view') ?? 'overview';
    const subTab: FinancialSubTab = (['overview', 'deposits', 'withdrawals', 'mix', 'ftd', 'players'] as const).includes(subTabRaw as FinancialSubTab)
      ? (subTabRaw as FinancialSubTab)
      : 'overview';

    const dateFrom = params.get('from') || defaultDateFrom();
    const dateTo = params.get('to') || defaultDateTo();

    const siteIdsStr = params.get('site_ids') || '';
    const siteIds = siteIdsStr
      ? siteIdsStr.split(',').map((s) => parseInt(s, 10)).filter((n) => !Number.isNaN(n))
      : [];

    const currencyRaw = params.get('display_currency') || 'EUR';
    const currencyMode: CurrencyMode = (['EUR', 'USD', 'BASE'] as const).includes(currencyRaw as CurrencyMode)
      ? (currencyRaw as CurrencyMode)
      : 'EUR';

    const pmTypeRaw = params.get('payment_method_type') || '';
    const pmType: PaymentMethodType | undefined = pmTypeRaw
      ? (pmTypeRaw as PaymentMethodType)
      : undefined;

    const granRaw = params.get('granularity') || 'daily';
    const granularity: 'daily' | 'weekly' | 'monthly' =
      granRaw === 'weekly' || granRaw === 'monthly' ? granRaw : 'daily';

    const dimsStr = params.get('dimensions') || '';
    const dimensions = dimsStr ? dimsStr.split(',').filter(Boolean) : [];

    const playerSearch = params.get('player_search') || '';

    return {
      subTab, dateFrom, dateTo, siteIds, currencyMode, pmType, granularity, dimensions, playerSearch,
      setSubTab: (v) => patch({ view: v }),
      setDateFrom: (v) => patch({ from: v }),
      setDateTo: (v) => patch({ to: v }),
      setDateRange: (from, to) => patch({ from, to }),
      setSiteIds: (v) => patch({ site_ids: v.length ? v.join(',') : null }),
      setCurrencyMode: (v) => patch({ display_currency: v }),
      setPmType: (v) => patch({ payment_method_type: v ?? null }),
      setGranularity: (v) => patch({ granularity: v === 'daily' ? null : v }),
      setDimensions: (v) => patch({ dimensions: v.length ? v.join(',') : null }),
      setPlayerSearch: (v) => patch({ player_search: v || null }),
    };
  }, [params, patch]);
}
